import { useState, useEffect, useRef, useCallback } from 'react';
import { aiAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const POLL_INTERVAL = 3000; // 3 seconds
const HISTORY_LIMIT = 3;     // Keep last 3 completed generations visible
const HISTORY_ALL_LIMIT = 50; // Fetch all generations

/**
 * useAiGeneration
 *
 * Manages the lifecycle of an AI generation job for a single sprite:
 *   - Submits generation requests to the backend
 *   - Polls for job status until completion
 *   - Exposes generated results for the user to pick from
 *   - Maintains a history of the last 3 completed jobs so the user
 *     can browse and select from previous generations
 *   - Handles result selection (from current or historical jobs)
 *   - Prevents duplicate generation while one is in-flight
 *   - Falls back to embedded `aiGenerationHistory` from componentData
 *     when the sprite was loaded from a template (no AiJob records for this user)
 *
 * @param {string|null} spriteId – The canvas element ID this hook tracks
 * @param {Array|null} embeddedHistory – Pre-embedded generation history from template componentData.aiGenerationHistory
 */
export const useAiGeneration = (spriteId, embeddedHistory = null) => {
  const { updateCredits } = useAuth();
  const [job, setJob] = useState(null);           // Current/latest job object
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);      // Last N completed jobs (newest first)
  const [showingAllHistory, setShowingAllHistory] = useState(false);
  // Track whether we've loaded API history at least once (to know if we should fall back to embedded)
  const apiHistoryLoadedRef = useRef(false);
  const pollRef = useRef(null);

  // ── Reset state & cleanup polling on spriteId change ──
  // Without this reset, switching between AI sprites briefly shows the
  // previous sprite's history / job / results until the async API calls
  // for the new spriteId complete — making it look like the two sprites
  // are "linked".
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      setJob(null);
      setIsGenerating(false);
      setError(null);
      setHistory([]);
      setShowingAllHistory(false);
      apiHistoryLoadedRef.current = false;
    };
  }, [spriteId]);

  // ── Fetch completed job history for this sprite ──
  const fetchHistory = useCallback(async (limit = HISTORY_LIMIT) => {
    if (!spriteId) return;
    try {
      const res = await aiAPI.getJobHistory(spriteId, limit);
      const apiJobs = res.data.data.jobs || [];
      apiHistoryLoadedRef.current = true;

      if (apiJobs.length > 0) {
        // User has their own AI jobs for this sprite.
        // Also append embedded history from template so previous generations
        // remain accessible (without linking to the original jobs).
        if (embeddedHistory && embeddedHistory.length > 0) {
          const apiJobIds = new Set(apiJobs.map(j => j._id));
          const uniqueEmbedded = embeddedHistory.filter(e => !apiJobIds.has(e._id));
          setHistory([...apiJobs, ...uniqueEmbedded]);
        } else {
          setHistory(apiJobs);
        }
      } else if (embeddedHistory && embeddedHistory.length > 0) {
        // No API jobs found — fall back to embedded history from template.
        // These are read-only snapshots (selecting from them uses a different path).
        setHistory(embeddedHistory);
      } else {
        setHistory([]);
      }
    } catch (err) {
      // Non-critical — history is a nice-to-have
      console.warn('Failed to fetch AI job history:', err);
      // On error, still try embedded history as fallback
      if (!apiHistoryLoadedRef.current && embeddedHistory && embeddedHistory.length > 0) {
        setHistory(embeddedHistory);
      }
    }
  }, [spriteId, embeddedHistory]);

  // ── Fetch all completed generations for this sprite ──
  const fetchAllHistory = useCallback(async () => {
    await fetchHistory(HISTORY_ALL_LIMIT);
    setShowingAllHistory(true);
  }, [fetchHistory]);

  // ── Collapse back to default history limit ──
  const collapseHistory = useCallback(async () => {
    await fetchHistory(HISTORY_LIMIT);
    setShowingAllHistory(false);
  }, [fetchHistory]);

  // ── Start polling for a given jobId ──
  const startPolling = useCallback((jobId) => {
    // Clear any existing poll
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = async () => {
      try {
        const res = await aiAPI.getJobStatus(jobId);
        const updatedJob = res.data.data.job;
        setJob(updatedJob);

        if (updatedJob.status === 'completed' || updatedJob.status === 'failed' || updatedJob.status === 'cancelled') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setIsGenerating(false);

          if (updatedJob.status === 'failed') {
            setError(updatedJob.error || 'Generation failed');
          }

          // Refresh history when a job completes so the new results appear
          if (updatedJob.status === 'completed') {
            fetchHistory();
          }
        }
      } catch (err) {
        console.error('AI poll error:', err);
        // Don't stop polling on transient errors
      }
    };

    // Immediate first poll, then interval
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, [fetchHistory]);

  // ── Reconnect: check if there's already an active job for this sprite ──
  useEffect(() => {
    if (!spriteId) return;

    const reconnect = async () => {
      try {
        const res = await aiAPI.getActiveJobForSprite(spriteId);
        const existingJob = res.data.data.job;
        if (existingJob) {
          setJob(existingJob);
          if (existingJob.status === 'pending' || existingJob.status === 'processing') {
            setIsGenerating(true);
            startPolling(existingJob._id);
          }
        }
      } catch (err) {
        // Sprite may not have a job yet — that's fine
      }
    };

    reconnect();
    // Also fetch history on mount / sprite change
    fetchHistory();
  }, [spriteId, startPolling, fetchHistory]);

  // ── Submit a new generation ──
  const generate = useCallback(async (payload) => {
    if (!spriteId) return null;
    if (isGenerating) return null; // Block duplicate

    setError(null);
    setIsGenerating(true);

    try {
      const res = await aiAPI.generate({
        spriteId,
        prompt: payload.prompt,
        settings: payload.settings,
        referenceMedia: payload.referenceMedia || [],
      });

      const newJob = res.data.data.job;
      const creditsRemaining = res.data.data.creditsRemaining;
      if (typeof creditsRemaining === 'number') {
        updateCredits(creditsRemaining);
      }
      setJob(newJob);
      startPolling(newJob._id);
      return newJob;
    } catch (err) {
      setIsGenerating(false);
      const data = err.response?.data;
      const message = data?.message || err.message || 'Failed to start generation';
      if (data?.code === 'INSUFFICIENT_CREDITS') {
        setError(`INSUFFICIENT_CREDITS:${message}`);
      } else {
        setError(message);
      }
      return null;
    }
  }, [spriteId, isGenerating, startPolling, updateCredits]);

  // ── Stop / cancel the current generation ──
  const stopGeneration = useCallback(async () => {
    if (!job?._id) return;
    if (!isGenerating) return;

    try {
      // Stop polling immediately
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      const res = await aiAPI.cancelJob(job._id);
      const cancelledJob = res.data.data.job;
      setJob(cancelledJob);
      setIsGenerating(false);
    } catch (err) {
      console.error('Failed to cancel AI job:', err);
      // Even if the server call fails, stop the client-side polling
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setIsGenerating(false);
      const message = err.response?.data?.message || err.message || 'Failed to cancel generation';
      setError(message);
    }
  }, [job, isGenerating]);

  // ── Select a result (from current job OR a historical job) ──
  // For embedded history entries (from templates), the jobId is a string snapshot
  // and the selectResult API call may fail (job belongs to another user).
  // In that case, we return the result directly from the local history so the
  // caller can still update the sprite media.
  const selectResult = useCallback(async (jobId, resultIndex) => {
    if (!jobId) return null;

    try {
      const res = await aiAPI.selectResult(jobId, resultIndex);
      const { media, selectedResult } = res.data.data;

      // Update local job state if it's the current job
      setJob((prev) => {
        if (prev && prev._id === jobId) {
          return { ...prev, selectedResultIndex: resultIndex, selectedMediaId: media._id };
        }
        return prev;
      });

      // Update history entry too
      setHistory((prev) =>
        prev.map((h) =>
          h._id === jobId
            ? { ...h, selectedResultIndex: resultIndex, selectedMediaId: media._id }
            : h
        )
      );

      return { media, selectedResult };
    } catch (err) {
      // If the API call fails (e.g. embedded history from template, job belongs to another user),
      // try to resolve the result from local history so the sprite media can still be updated.
      const localJob = history.find((h) => h._id === jobId);
      if (localJob && localJob.results && localJob.results[resultIndex]) {
        const selectedResult = localJob.results[resultIndex];

        // Update local history to reflect the selection
        setHistory((prev) =>
          prev.map((h) =>
            h._id === jobId
              ? { ...h, selectedResultIndex: resultIndex }
              : h
          )
        );

        // Return a compatible shape so the caller can update the sprite
        return {
          media: { originalName: localJob.prompt || 'AI Generation' },
          selectedResult,
        };
      }

      const message = err.response?.data?.message || err.message || 'Failed to select result';
      setError(message);
      return null;
    }
  }, [history]);

  return {
    job,
    isGenerating,
    error,
    results: job?.results || [],
    selectedResultIndex: job?.selectedResultIndex ?? null,
    history,             // Array of completed jobs (newest first), each with .results, .prompt, .settings, etc.
    showingAllHistory,   // Whether all history has been loaded
    generate,
    stopGeneration,      // Cancel the current in-progress/queued job
    selectResult,        // Now accepts (jobId, resultIndex) instead of just (resultIndex)
    fetchHistory,
    fetchAllHistory,     // Load all generations for this sprite
    collapseHistory,     // Collapse back to default history limit
  };
};
